import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Download, Printer, ShieldCheck } from 'lucide-react';
import pipelineService, { PipelineRun, ExtractedField } from '../../services/pipelineService';
import docshawkService, { CandidateField } from '../../services/docshawkService';
import { Spinner, Button } from '../../components/ui';
import '../../styles/theme.css';
import '../../styles/review-theme.css';
import '../../styles/review-workspace.css';

function reconcile(fields: ExtractedField[]): Record<string, ExtractedField> {
  const best: Record<string, ExtractedField> = {};
  const rank = (x: ExtractedField) => (x.grounded ? 10 : 0) + (x.confidence || 0);
  (fields || []).forEach((f) => {
    if (f.value == null || f.value === '') return;
    const cur = best[f.field_key];
    if (!cur || rank(f) > rank(cur)) best[f.field_key] = f;
  });
  return best;
}

const RunResults: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<PipelineRun | null>(null);
  const [schema, setSchema] = useState<CandidateField[]>([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!runId) return;
    pipelineService.getRun(runId).then((r) => setRun(r.run)).catch(() => setErr('Couldn’t load this document.'));
    docshawkService.getActiveSchema().then((r) => setSchema(r.current_version?.fields || [])).catch(() => {});
  }, [runId]);

  const best = useMemo(() => reconcile(run?.extracted_fields || []), [run]);
  const keys = useMemo(() => {
    const s = new Set<string>();
    schema.forEach((f) => f.key && s.add(f.key));
    Object.keys(best).forEach((k) => s.add(k));
    return Array.from(s);
  }, [schema, best]);
  const labelOf = (k: string) => schema.find((s) => s.key === k)?.label || k;
  const verified = keys.filter((k) => best[k]?.grounded).length;
  const found = keys.filter((k) => best[k] && best[k].value).length;

  if (err) return <div className="dh-review"><div className="mx-card mx-card-pad mx-muted">{err}</div></div>;
  if (!run) return <div className="dh-review"><div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div></div>;

  const download = async () => {
    const text = await pipelineService.downloadExport(run.id);
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `docshawk_${run.id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dh-review dh-results">
      <Button icon={ArrowLeft} variant="ghost" size="sm" className="dh-noprint" style={{ marginBottom: 12, paddingLeft: 4 }}
        onClick={() => navigate(`/admin/docshawk/runs/${run.id}`)}>Back to review</Button>

      <div className="dh-panel" style={{ padding: 0 }}>
        <div style={{ padding: '18px 20px', display: 'flex', gap: 13, alignItems: 'flex-start', borderBottom: '1px solid var(--dh-line)' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--dh-verified-bg)', color: 'var(--dh-verified)',
            display: 'grid', placeItems: 'center', flex: 'none' }}><Check size={22} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--dh-serif)', fontSize: 21, fontWeight: 600 }}>{run.document_filename}</div>
            <div style={{ fontSize: 13, color: 'var(--dh-ink-soft)', marginTop: 2 }}>
              Review complete · {found} terms · {verified} verified in the document ·{' '}
              {run.updated_at ? new Date(run.updated_at).toLocaleDateString() : ''}</div>
          </div>
          <div className="dh-noprint" style={{ display: 'flex', gap: 8 }}>
            <Button icon={Printer} tone="paper" variant="secondary" style={{ border: '1px solid var(--dh-line-strong)' }}
              onClick={() => window.print()}>Save as PDF</Button>
            <Button icon={Download} tone="paper" variant="primary" onClick={download}>CSV</Button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="dh-rtable">
            <thead><tr><th>Term</th><th>Value</th><th>Source in document</th></tr></thead>
            <tbody>
              {keys.map((k) => {
                const f = best[k];
                const hasVal = f && f.value != null && f.value !== '';
                return (
                  <tr key={k}>
                    <td className="term">{labelOf(k)}</td>
                    <td className="val">
                      {hasVal ? <>{f.value}<span className={`tag ${f.grounded ? 'ok' : 'rev'}`}>
                        {f.grounded ? '✓ verified' : '⚠ your review'}</span></>
                        : <span className="na">— not present in this agreement</span>}
                    </td>
                    <td className="src">
                      {hasVal && f.provenance?.page ? <span className="pg">p. {f.provenance.page}</span> : null}
                      {hasVal && f.provenance?.source_snippet
                        ? <span className="q">“{f.provenance.source_snippet.slice(0, 140)}”</span>
                        : hasVal ? <span className="na">—</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '13px 20px', borderTop: '1px solid var(--dh-line)', display: 'flex', gap: 9, alignItems: 'flex-start',
          fontSize: 12.5, color: 'var(--dh-ink-soft)' }}>
          <ShieldCheck size={16} style={{ color: 'var(--dh-verified)', flex: 'none', marginTop: 1 }} />
          Every value includes the page and exact passage it came from — ready for an audit trail or diligence file.
        </div>
      </div>
    </div>
  );
};

export default RunResults;
